import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCwIcon } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

import { Button } from "@/components/base/button";
import { OverrideScoreForm } from "@/components/matches/director-controls/override-score";
import { ProfileName } from "@/components/profiles/name";
import { RefsList } from "@/components/refs/refs-list";
import type {
  MatchRef,
  MatchSet,
  PlayerProfile,
  Pool,
  PoolMatch,
  PoolTeam,
  Team,
  TournamentDivisionTeam,
} from "@/db/schema";
import { getViewerProfilesQueryOptions } from "@/functions/profiles/get-viewer-profiles";
import { useTRPC } from "@/integrations/trpc/react";
import { formatScheduledTime } from "@/lib/dates";
import { getSetScore, hasInProgressSet, isMatchLive } from "@/lib/matches";
import { isDefined, isNotNull } from "@/utils/types";

import {
  useActiveDivisionId,
  useCanUpdateTournament,
  useIsDemoTournament,
} from "../../context";
import { SimulateMatchModal } from "../../controls/simulate-match";

export const scoreTextStyles = tv({
  base: "p-2 md:p-4 flex flex-col justify-center col-span-3 md:col-span-1 text-xl font-light border-gray-300",
  variants: {
    winner: {
      true: "",
      false: "",
    },
    inProgress: {
      true: "font-bold",
    },
    last: {
      true: ["border-r-0"],
      false: ["border-r"],
    },
  },
  compoundVariants: [
    {
      winner: false,
      inProgress: false,
      class: "text-gray-400",
    },
  ],
});

export type MatchTeam = Pick<
  TournamentDivisionTeam,
  "id" | "playoffsSeed" | "wildcard"
> & {
  team: Pick<Team, "id"> & {
    players: {
      profile: Pick<
        PlayerProfile,
        "id" | "preferredName" | "firstName" | "lastName"
      >;
    }[];
  };
  poolTeam: Pick<PoolTeam, "seed" | "finish"> & { pool: Pick<Pool, "name"> };
};

const headerStyles = tv({
  base: "grid grid-cols-10 items-center text-navbar-foreground",
  variants: {
    viewerRole: {
      reffing: "bg-red-700",
      playing: "bg-blue-200 text-blue-900",
      none: "bg-navbar-background",
    },
  },
  defaultVariants: {
    viewerRole: "none",
  },
});

export const PoolMatchGrid = memo(function PoolMatchGrid({
  id,
  status,
  sets,
  matchNumber,
  scheduledTime,
  forfeitTeamId,
  winnerId,
  teamA,
  teamB,
  refs,
  viewerIsRef,
  refetch,
}: Pick<
  PoolMatch,
  | "id"
  | "winnerId"
  | "matchNumber"
  | "status"
  | "scheduledTime"
  | "forfeitTeamId"
> & {
  sets: Pick<
    MatchSet,
    | "id"
    | "teamAScore"
    | "teamBScore"
    | "winnerId"
    | "startedAt"
    | "endedAt"
    | "setNumber"
    | "status"
    | "winScore"
    | "switchScore"
  >[];
  teamA: MatchTeam | null;
  teamB: MatchTeam | null;
  refs: (MatchRef & {
    profile: Pick<
      PlayerProfile,
      "id" | "preferredName" | "firstName" | "lastName"
    >;
  })[];
  viewerIsRef: boolean;
  refetch: () => void;
}) {
  const trpc = useTRPC();
  const tournamentDivisionId = useActiveDivisionId();
  const isDemo = useIsDemoTournament();
  const canUpdate = useCanUpdateTournament();
  const [overrideId, setOverrideId] = useState<number | undefined>(undefined);

  const { data: scoreEventsMap, refetch: refetchScoreEvents } = useQuery({
    ...trpc.matches.getScoreEvents.queryOptions({ poolMatchId: id }),
    enabled: hasInProgressSet({ status, sets }),
  });

  const { data: viewerProfiles } = useQuery(getViewerProfilesQueryOptions());

  const viewerProfileIds = useMemo(
    () => new Set(viewerProfiles?.map((p) => p.id)),
    [viewerProfiles],
  );

  const viewerRole = useMemo(() => {
    if (viewerIsRef) return "reffing";

    const allPlayers = [
      ...(teamA?.team.players ?? []),
      ...(teamB?.team.players ?? []),
    ];

    if (allPlayers.some((p) => viewerProfileIds.has(p.profile.id)))
      return "playing";

    return "none";
  }, [viewerIsRef, teamA, teamB, viewerProfileIds]);

  return (
    <div className="w-full overflow-hidden rounded-md border border-gray-300">
      <div className={headerStyles({ viewerRole })}>
        <div
          className={twMerge(
            "p-3 flex flex-row flex-wrap items-center gap-2 min-w-0 overflow-hidden",
            `col-span-10 ${sets.length > 1 ? "md:col-span-7" : "md:col-span-9"} md:gap-3`,
          )}
        >
          <div className="flex flex-row items-center gap-2">
            <Link
              to="/matches/pool/$matchId"
              className="underline"
              params={{ matchId: id.toString() }}
            >
              {sets.length > 1 ? "Match" : "Game"} {matchNumber}
            </Link>
            {canUpdate &&
              isDemo &&
              teamA &&
              teamB &&
              status !== "completed" && (
                <SimulateMatchModal
                  tournamentDivisionId={tournamentDivisionId}
                  poolMatchId={id}
                />
              )}
            {forfeitTeamId && (
              <span className="text-xs font-medium text-red-400">Forfeit</span>
            )}
            {scheduledTime && (
              <span className="text-xs opacity-70">
                {formatScheduledTime(scheduledTime)}
              </span>
            )}
          </div>

          {isMatchLive({ status, sets }) ? (
            <LiveRefetchButton
              refetch={() => {
                refetch();
                refetchScoreEvents();
              }}
            />
          ) : null}

          <RefsList
            tournamentDivisionId={tournamentDivisionId}
            poolMatchId={id}
            matchStatus={isDefined(teamA) && isDefined(teamB) ? status : "tbd"}
            refs={refs}
            isDemo={isDemo}
          />
        </div>
        {sets.map((s) => (
          <div
            key={s.id}
            className={twMerge(
              "px-1 py-3 text-center text-sm self-end",
              "hidden md:block col-span-1",
            )}
          >
            {canUpdate ? (
              <Button
                variant={canUpdate ? "link" : "text"}
                color="alternate"
                className="p-0 text-inherit"
                onPress={
                  canUpdate
                    ? () => {
                        setOverrideId(s.id);
                      }
                    : undefined
                }
                tooltip={canUpdate ? "Click to override score" : undefined}
              >
                {sets.length > 1 ? <>Set {s.setNumber}</> : "Score"}
              </Button>
            ) : (
              <span>{sets.length > 1 ? <>Set {s.setNumber}</> : "Score"}</span>
            )}

            {overrideId === s.id && (
              <OverrideScoreForm
                tournamentDivisionId={tournamentDivisionId}
                poolMatchId={id}
                setId={s.id}
                isOpen
                onOpenChange={(open) => {
                  if (!open) {
                    setOverrideId(undefined);
                  }
                }}
              />
            )}
          </div>
        ))}
      </div>
      {[teamA, teamB].filter(isNotNull).map((team) => {
        const isForfeited = forfeitTeamId === team.id;
        return (
          <div
            key={team.id}
            className={twMerge(
              "grid grid-cols-10 items-stretch border-b border-gray-300 bg-white last-of-type:border-b-0",
              isForfeited && "opacity-50",
            )}
          >
            <div
              className={twMerge(
                "p-2 md:p-4 flex flex-row space-x-2 items-center",
                sets.length > 1 ? "col-span-7" : "col-span-9",
              )}
            >
              <span
                className="hidden rounded-xs bg-gray-200 p-2 md:inline"
                title={`Seed ${team.poolTeam?.seed ?? "?"}`}
              >
                #{team.poolTeam?.seed ?? "?"}
              </span>
              <span className="text-sm text-gray-500 md:hidden">
                {team.poolTeam?.seed ?? "?"}
              </span>
              <div
                className={twMerge(
                  "flex flex-col",
                  winnerId && winnerId === team.id && "font-bold",
                  winnerId && winnerId !== team.id && "text-gray-400",
                )}
              >
                {team?.team.players.map(({ profile }) => (
                  <ProfileName {...profile} key={profile.id} />
                ))}
              </div>
              {isForfeited && (
                <span className="text-xs font-medium text-red-500">FF</span>
              )}
            </div>
            {sets
              .sort((a, b) => a.setNumber - b.setNumber)
              .map((s, i) => {
                const score = getSetScore({
                  ...s,
                  scoreEvents: scoreEventsMap?.[s.id],
                });
                const isTeamA = teamA?.id === team.id;
                const winner =
                  s.status === "completed"
                    ? (score.a > score.b && isTeamA) ||
                      (score.a < score.b && !isTeamA)
                    : undefined;

                return (
                  <div
                    key={s.id}
                    className={twMerge(
                      scoreTextStyles({
                        winner,
                        inProgress: Boolean(s.startedAt) && !s.endedAt,
                        last: i === sets.length - 1,
                      }),
                      "col-span-1 text-base md:text-xl p-1 md:p-4 text-center",
                    )}
                  >
                    {s.status === "not_started" ||
                    (status === "completed" && s.status !== "completed")
                      ? "-"
                      : isTeamA
                        ? score.a
                        : score.b}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
});

export function LiveRefetchButton({
  refetch,
  isDisabled,
}: {
  refetch: () => void;
  isDisabled?: boolean;
}) {
  const [spinning, setSpinning] = useState(false);

  const handlePress = useCallback(() => {
    refetch();
    setSpinning(true);
    setTimeout(() => setSpinning(false), 700);
  }, [refetch]);

  return (
    <Button
      size="sm"
      className="rounded-sm px-2"
      color="primary"
      isDisabled={isDisabled}
      tooltip={<>Click to refresh</>}
      onClick={handlePress}
    >
      <RefreshCwIcon
        size={18}
        className={spinning ? "animate-spin" : undefined}
      />
      Live
    </Button>
  );
}
